import React, { useEffect, useState, useMemo } from "react";
import Chart from "react-apexcharts";
import { RevenueIcon, SortDownArrow } from "../../assets/svg";
import Breadcrumb from "../../components/common/BreadCrumb";
import { ExportButton, SelectWithLabel } from "../../components/micro/input";
import CustomTable from "../../components/customTable/CustomTable";
import { getSalesReportList } from "../../redux/reports/slice";
import { useDispatch, useSelector } from "react-redux";
import RippleLoader from "../../components/loader/RippleLoader";
import ReactDatePicker from "react-datepicker";
import moment from "moment";
import { companiesList } from "../../redux/companies/slice";
import ReactSelect from "react-select";
import { useNavigate } from "react-router-dom";

const SalesReports = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const currentYear = new Date().getFullYear();
  const [year, setYear] = useState(currentYear.toString());
  const [pageLimit, setPageLimit] = useState(10);
  const [pageCount, setPageCount] = useState(1);
  const [sortBy, setSortBy] = useState("");
  const [sortDirection, setSortDirection] = useState("desc");
  const [company, setCompany] = useState("");
  const [dateRange, setDateRange] = useState([null, null]);
  const [technicianPage, setTechnicianPage] = useState(1);
  const [technicianPageLimit, setTechnicianPageLimit] = useState(10);

  const DateRange =
    dateRange[0] && dateRange[1]
      ? `${moment(dateRange[0]).format("DD/MM/YYYY")} - ${moment(
          dateRange[1]
        ).format("DD/MM/YYYY")}`
      : "";

  // Selector
  const { getSalesReportListData, loading, companiesListData } = useSelector(
    (store) => ({
      getSalesReportListData:
        store?.salesReportData?.getSalesReportListStatus?.data,
      loading: store?.salesReportData?.loading,
      companiesListData: store?.companiesData?.companiesListStatus?.data?.data,
    })
  );

  useEffect(() => {
    dispatch(
      getSalesReportList({
        pageCount: pageCount,
        pageLimit: pageLimit,
        sort_by: sortBy,
        sort_direction: sortDirection === "asc" ? "asc" : "desc",
        date_range: DateRange || "",
        year: year || "",
        company: company?.label || "",
        technician_earnings_page: technicianPage,
        technician_earnings_per_page: technicianPageLimit,
      })
    );
    dispatch(
      companiesList({
        search: "",
        pageLimit: "all",
        status: "",
        sort_by: "name",
        sort_direction: "desc",
        contract: "all",
      })
    );
  }, [
    dispatch,
    pageCount,
    pageLimit,
    sortBy,
    sortDirection,
    DateRange,
    year,
    company,
    technicianPage,
    technicianPageLimit,
  ]);

  const yearOptions = Array.from({ length: 5 }, (_, i) => ({
    label: (currentYear - i).toString(),
    value: (currentYear - i).toString(),
  }));

  const handleYearChange = (e) => {
    setYear(e.target.value);
  };

  const chartData = {
    options: {
      chart: {
        type: "bar",
        toolbar: {
          show: true,
          tools: {
            download: false,
          },
        },
      },
      plotOptions: {
        bar: {
          horizontal: false,
          columnWidth: "60%",
          borderRadius: 2,
          endingShape: "rounded",
          dataLabels: {
            position: "top",
          },
        },
      },
      dataLabels: {
        enabled: false,
      },
      fill: {
        type: "gradient",
        gradient: {
          shade: "light",
          type: "vertical",
          opacityFrom: 1,
          opacityTo: 0.7,
          stops: [0, 100],
        },
      },
      colors: ["#2E7D6F", "#F15B29"],
      legend: {
        show: true,
        position: "top",
        horizontalAlign: "right",
        markers: {
          width: 12,
          height: 12,
          radius: 2,
        },
      },
      grid: {
        borderColor: "#E0E0E0",
        strokeDashArray: 4,
      },
      xaxis: {
        categories:
          getSalesReportListData?.data?.sales_Revenue?.map(
            (item) => item?.month
          ) || [],
        labels: {
          style: {
            colors: "#333",
            fontSize: "12px",
            fontWeight: 500,
          },
        },
      },
      yaxis: {
        labels: {
          style: {
            colors: "#00000099",
            fontSize: "14px",
          },
          formatter: (value) => {
            if (value === null || value === undefined || isNaN(value))
              return "$0";
            return `$${Number(value).toLocaleString()}`;
          },
        },
      },
      tooltip: {
        custom: function ({ series, seriesIndex, dataPointIndex, w }) {
          const month = w.globals.categoryLabels[dataPointIndex];
          const seriesName = w.config.series[seriesIndex].name;
          const value = series[seriesIndex][dataPointIndex];
          const revenueData = getSalesReportListData?.data?.sales_Revenue?.[dataPointIndex];
          
          const isNegative = revenueData?.last_month_to_current_month?.includes("-");
          const growthValue = revenueData?.last_month_to_current_month?.replace("-", "") || "0%";
          const arrowColor = isNegative ? "#F04438" : "#2AD192";
          const barColor = seriesIndex === 0 ? "#2E7D6F" : "#F15B29";

          const arrowPath = isNegative
            ? "M12 22L5 15H10V2H14V15H19L12 22Z" // ↓
            : "M12 2L19 9H14V22H10V9H5L12 2Z"; // ↑

          return `
            <div style="
              background: #000;
              color: white;
              border-radius: 12px;
              padding: 12px 16px;
              font-family: 'Inter', sans-serif;
              font-size: 14px;
              box-shadow: 0px 4px 12px rgba(0,0,0,0.15);
              width: 180px;
              display: flex;
              flex-direction: column;
            ">
              <div style="font-weight: 500; font-size: 14px;">${month} ${year}</div>
              <div style="font-size: 20px; font-weight: 500; margin: 4px 0; color: ${barColor};">$${Number(value).toLocaleString()}</div>
              <div style="font-size: 12px; color: #FFA07A;">${seriesName}</div>
              ${revenueData?.last_month_to_current_month ? `
              <div style="margin-top: 6px;">
                <span style="
                  display: inline-flex;
                  align-items: center;
                  gap: 6px;
                  vertical-align: middle;
                ">
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="${arrowColor}" xmlns="http://www.w3.org/2000/svg">
                    <path d="${arrowPath}"/>
                  </svg>
                  <span style="color: ${arrowColor}; font-weight: 500;">
                    ${growthValue}
                  </span>
                </span>
              </div>
              ` : ''}
            </div>
          `;
        },
      },
    },
    series: [
      {
        name: "Paid",
        data:
          getSalesReportListData?.data?.sales_Revenue?.map((item) => {
            // Convert string with commas to number for paid_amount
            const paidAmount = item?.paid_amount
              ? parseFloat(String(item.paid_amount).replace(/,/g, ""))
              : 0;
            return paidAmount;
          }) || [],
      },
      {
        name: "Unpaid",
        data:
          getSalesReportListData?.data?.sales_Revenue?.map((item) => {
            // Convert string with commas to number for unpaid_amount
            const unpaidAmount = item?.unpaid_amount
              ? parseFloat(String(item.unpaid_amount).replace(/,/g, ""))
              : 0;
            return unpaidAmount;
          }) || [],
      },
    ],
  };

  const handleSalesRevenueExportClick = () => {
    const revenueData = getSalesReportListData?.data?.sales_Revenue;

    if (!revenueData || revenueData.length === 0) {
      alert("No chart data available to export!");
      return;
    }

    const header = ["Month", "Sales Amount", "Growth"];

    const rows = revenueData.map((item) => [
      `"${item?.month || "-"}"`,
      `"${item?.sales_amount || "-"}"`,
      `"${item?.last_month_to_current_month || "-"}"`,
    ]);

    let csvContent = header.join(",") + "\n";
    rows.forEach((row) => {
      csvContent += row.join(",") + "\n";
    });

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    if (link.download !== undefined) {
      const url = URL.createObjectURL(blob);
      link.setAttribute("href", url);
      link.setAttribute("download", "sales_revenue_chart.csv");
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const handleSalesBreakdownExportClick = () => {
    const salesData = getSalesReportListData?.data?.sales?.data;

    if (!salesData || salesData.length === 0) {
      alert("No sales data available to export!");
      return;
    }

    const header = ["No.", "Company Name", "Invoice ID", "Amount", "Date"];

    const rows = salesData.map((item, index) => [
      `"${index + 1}"`,
      `"${item?.company_name || "-"}"`,
      `"${item?.autocount_invoice_id || "-"}"`,
      `"${item?.amount?.toLocaleString() || "-"}"`,
      `"${item?.date || "-"}"`,
    ]);

    let csvContent = header.join(",") + "\n";
    rows.forEach((row) => {
      csvContent += row.join(",") + "\n";
    });

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    if (link.download !== undefined) {
      const url = URL.createObjectURL(blob);
      link.setAttribute("href", url);
      link.setAttribute("download", "sales_breakdown.csv");
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const Revenue = () => (
    <div className="bg-white rounded-lg shadow p-6 border border-borderLine">
      <div className="flex justify-between items-center">
        <div className="flex items-center gap-2">
          <RevenueIcon className="w-8 h-8" />
          <h2 className="text-2xl font-normal font-Montserrat text-primaryBlacks">
            Sales Revenue
          </h2>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-24">
            <SelectWithLabel
              className="w-full py-2"
              placeholder="Year"
              options={yearOptions}
              value={year}
              onChange={handleYearChange}
            />
          </div>
          <ExportButton
            value="EXPORT CSV"
            className="-mt-1"
            onClick={handleSalesRevenueExportClick}
          />
        </div>
      </div>

      <div className="mt-4">
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-2">
            <h3 className="text-[34px] text-primaryBlack font-normal font-Montserrat">
              ${getSalesReportListData?.data?.TotalRevenue.toLocaleString()}
            </h3>
            <p className="text-textSecondary text-sm font-Proxima">Earned</p>
          </div>

          <div className="flex items-center gap-1">
            <span
              className={`text-xs text-white py-1 px-2 rounded-3xl flex items-center ${
                getSalesReportListData?.data?.last_year_to_current_year?.includes(
                  "-"
                )
                  ? "bg-red"
                  : "bg-bgGreen"
              }`}
            >
              {getSalesReportListData?.data?.last_year_to_current_year?.startsWith(
                "-"
              )
                ? getSalesReportListData?.data?.last_year_to_current_year.replace(
                    "-",
                    ""
                  )
                : getSalesReportListData?.data?.last_year_to_current_year}
            </span>
            <span className="text-xs">
              vs <span className="text-themeBtn">Last Year</span>
            </span>
          </div>
        </div>

        <div className="mt-4">
          <Chart
            options={chartData.options}
            series={chartData.series}
            height="300"
            type="bar"
          />
        </div>
      </div>
    </div>
  );

  const ColumnHeaders = () => (
    <>
      <th
        scope="col"
        className="p-2 font-Proxima text-sm font-normal py-4 px-4 text-mgrey text-start"
      >
        <span>No.</span>
      </th>
      <th
        scope="col"
        className="p-2 font-Proxima text-sm font-normal py-4 px-4 text-mgrey text-start"
      >
        <div
          className="flex items-center"
          onClick={() => {
            setSortBy("company_name");
            setSortDirection(sortDirection === "desc" ? "asc" : "desc");
          }}
        >
          <span>Company</span>
          <SortDownArrow
            className={`ml-1 transition-transform duration-300 ${
              sortDirection === "asc" && sortBy === "company_name"
                ? "rotate-180"
                : ""
            }`}
          />
        </div>
      </th>
      <th className="p-2 font-Proxima text-sm font-normal py-4 px-4 text-mgrey text-start">
        <div
          className="flex items-center"
          onClick={() => {
            setSortBy("unique_id");
            setSortDirection(sortDirection === "desc" ? "asc" : "desc");
          }}
        >
          Receipt Id
          <SortDownArrow
            className={`ml-1 transition-transform duration-300 ${
              sortDirection === "asc" && sortBy === "unique_id"
                ? "rotate-180"
                : ""
            }`}
          />
        </div>
      </th>
      <th
        scope="col"
        className="p-2 font-Proxima text-sm font-normal py-4 px-4 text-mgrey text-start"
      >
        <div
          className="flex items-center"
          onClick={() => {
            setSortBy("amount");
            setSortDirection(sortDirection === "desc" ? "asc" : "desc");
          }}
        >
          <span>Price</span>
          <SortDownArrow
            className={`ml-1 transition-transform duration-300 ${
              sortDirection === "asc" && sortBy === "amount" ? "rotate-180" : ""
            }`}
          />
        </div>
      </th>
      <th
        scope="col"
        className="p-2 font-Proxima text-sm font-normal py-4 px-4 text-mgrey text-start"
      >
        <div
          className="flex items-center"
          onClick={() => {
            setSortBy("date");
            setSortDirection(sortDirection === "desc" ? "asc" : "desc");
          }}
        >
          <span>Date</span>
          <SortDownArrow
            className={`ml-1 transition-transform duration-300 ${
              sortDirection === "asc" && sortBy === "date" ? "rotate-180" : ""
            }`}
          />
        </div>
      </th>
    </>
  );

  const DataRows = () => {
    return getSalesReportListData?.data?.sales?.data?.map((item, index) => (
      <tr
        key={item?.id || index}
        className="font-Proxima text-[14px] shadow-sm text-mgrey hover:bg-lightprimaryorange"
      >
        <td className="p-4 text-sm text-primaryBlack">{index + 1}</td>
        <td className="p-4 text-sm text-primaryBlack">
          {item?.company_name || "-"}
        </td>
        <td
          className="p-4 text-sm text-themeBtn underline cursor-pointer"
          onClick={() => navigate("/sales", { state: { tab: "Receipt" } })}
        >
          {item?.unique_id || "-"}
        </td>
        <td className="p-4 text-sm text-primaryBlack">
          ${item?.amount?.toLocaleString() || "-"}
        </td>
        <td className="p-4 text-sm text-primaryBlack">{item?.date || "-"}</td>
      </tr>
    ));
  };

  const companyOptions = (companiesListData?.data || companiesListData)?.map(
    (companies) => ({
      value: companies.id,
      label: companies.name,
    })
  );

  const handleCompanyChange = (selectedOption) => {
    setCompany(selectedOption);
  };

  const totalSales =
    dateRange?.[0] && dateRange?.[1]
      ? getSalesReportListData?.data?.sales?.data?.reduce((acc, sale) => {
          const saleDate = moment(sale?.date, "DD/MM/YYYY");
          const isInRange = saleDate.isBetween(
            dateRange[0],
            dateRange[1],
            "day",
            "[]"
          );
          const amount = Number(sale?.amount);

          return isInRange && !isNaN(amount) ? acc + amount : acc;
        }, 0)
      : getSalesReportListData?.data?.sales?.Total_Sales || 0;

  const formatTime = (time) =>
    time ? moment(time, "HH:mm:ss").format("hh:mm A") : null;

  const technicianWorkingHours = (technician) => {
    if (!technician) return null;
    const start = formatTime(technician?.start_time);
    const end = formatTime(technician?.end_time);

    if (start && end) {
      return `${start} - ${end}`;
    }
    if (start) {
      return start;
    }
    if (end) {
      return end;
    }
    return null;
  };

  const technicianEarningsMeta =
    getSalesReportListData?.data?.technicianEarnings || {};
  const technicianEarningsData = useMemo(
    () => technicianEarningsMeta?.data || [],
    [technicianEarningsMeta?.data]
  );
  const totalTechnicianEarnings = technicianEarningsData.reduce(
    (acc, earning) => acc + Number(earning?.amount || 0),
    0
  );

  const technicianEarningsTableData = useMemo(() => {
    const total =
      Number(technicianEarningsMeta?.total) || technicianEarningsData.length;
    const perPage = technicianPageLimit || 10;
    const lastPage =
      Number(technicianEarningsMeta?.last_page) ||
      Math.max(1, Math.ceil(total / perPage));

    return {
      data: technicianEarningsData,
      total,
      per_page: perPage,
      current_page: technicianPage,
      last_page: lastPage,
    };
  }, [
    technicianEarningsData,
    technicianEarningsMeta?.total,
    technicianEarningsMeta?.last_page,
    technicianPageLimit,
    technicianPage,
  ]);

  const handleTechnicianPageChange = (page) => {
    const parsed = Number(page);
    if (!Number.isNaN(parsed) && parsed >= 1 && parsed !== technicianPage) {
      setTechnicianPage(parsed);
    }
  };

  const handleTechnicianPageLimitChange = (limit) => {
    const parsed = Number(limit);
    if (!Number.isNaN(parsed) && parsed > 0 && parsed !== technicianPageLimit) {
      setTechnicianPageLimit(parsed);
      setTechnicianPage(1);
    }
  };

  const TechnicianEarningsHeaders = () => (
    <>
      <th className="p-2 font-Proxima text-sm font-normal py-4 px-4 text-mgrey text-start">
        <span>No.</span>
      </th>
      <th className="p-2 font-Proxima text-sm font-normal py-4 px-4 text-mgrey text-start">
        <span>Technician</span>
      </th>
      <th className="p-2 font-Proxima text-sm font-normal py-4 px-4 text-mgrey text-start">
        <span>Company</span>
      </th>
      <th className="p-2 font-Proxima text-sm font-normal py-4 px-4 text-mgrey text-start">
        <span>Mobile</span>
      </th>
      <th className="p-2 font-Proxima text-sm font-normal py-4 px-4 text-mgrey text-start">
        <span>Email</span>
      </th>
      <th className="p-2 font-Proxima text-sm font-normal py-4 px-4 text-mgrey text-start">
        <span>Working Hours</span>
      </th>
      <th className="p-2 font-Proxima text-sm font-normal py-4 px-4 text-mgrey text-start">
        <span>Earnings</span>
      </th>
    </>
  );

  const TechnicianEarningsRows = () =>
    technicianEarningsData.map((earning, index) => (
      <tr
        key={earning?.id || index}
        className="font-Proxima text-[14px] shadow-sm text-mgrey hover:bg-lightprimaryorange"
      >
        <td className="p-4 text-sm text-primaryBlack">{index + 1}</td>
        <td className="p-4 text-sm text-primaryBlack">
          {earning?.technician?.full_name ||
            earning?.technician?.user?.full_name ||
            "-"}
        </td>
        <td className="p-4 text-sm text-primaryBlack">
          {earning?.technician?.company_name || earning?.company_name || "-"}
        </td>
        <td className="p-4 text-sm text-primaryBlack">
          {earning?.technician?.phone ||
            earning?.technician?.user?.phone ||
            "-"}
        </td>
        <td className="p-4 text-sm text-primaryBlack">
          {earning?.technician?.user?.email ||
            earning?.technician?.email ||
            "-"}
        </td>
        <td className="p-4 text-sm text-primaryBlack">
          {technicianWorkingHours(earning?.technician) ||
            (earning?.working_hours ? `${earning.working_hours} hrs` : "-")}
        </td>
        <td className="p-4 text-sm text-primaryBlack">
          {earning?.amount
            ? `$${Number(earning.amount).toLocaleString(undefined, {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}`
            : "-"}
        </td>
      </tr>
    ));

  const pnlChartData = useMemo(() => {
    const profitLossRevenue =
      getSalesReportListData?.data?.profit_loss_revenue || [];
    const categories = profitLossRevenue.map((item) => item?.month) || [];
    const chartData = profitLossRevenue.map((item) => {
      const amount =
        parseFloat(String(item?.sales_amount || "0").replace(/,/g, "")) || 0;
      return {
        x: item?.month,
        y: amount,
        growth: item?.last_month_to_current_month || "0%",
      };
    });

    return {
      options: {
        chart: {
          type: "bar",
          toolbar: {
            show: true,
            tools: {
              download: false,
            },
          },
        },
        plotOptions: {
          bar: {
            horizontal: false,
            columnWidth: "90%",
            borderRadius: 2,
            endingShape: "rounded",
            distributed: true,
          },
        },
        fill: {
          type: "gradient",
          gradient: {
            shade: "light",
            type: "vertical",
            gradientToColors: chartData.map((item) =>
              item.y >= 0 ? "rgba(42, 209, 146, 0.4)" : "rgba(240, 68, 56, 0.4)"
            ),
            opacityFrom: 1,
            opacityTo: 0.2,
            stops: [0, 100],
          },
        },
        colors: chartData.map((item) => (item.y >= 0 ? "#2AD192" : "#F04438")),
        dataLabels: {
          enabled: false,
        },
        legend: {
          show: false,
        },
        grid: {
          borderColor: "#E0E0E0",
          strokeDashArray: 4,
        },
        xaxis: {
          categories: categories,
          labels: {
            style: {
              colors: "#333",
              fontSize: "12px",
              fontWeight: 500,
            },
          },
        },
        yaxis: {
          labels: {
            style: {
              colors: "#00000099",
              fontSize: "14px",
            },
            formatter: (value) => {
              if (value === null || value === undefined || isNaN(value))
                return "$0";
              return `$${Number(value).toLocaleString()}`;
            },
          },
        },
        tooltip: {
          custom: function ({ series, seriesIndex, dataPointIndex, w }) {
            if (
              !w ||
              !w.config ||
              !w.config.series ||
              !w.config.series[seriesIndex]
            ) {
              return "";
            }
            const data = w.config.series[seriesIndex].data[dataPointIndex];
            if (
              !data ||
              data.y === null ||
              data.y === undefined ||
              isNaN(data.y)
            ) {
              return "";
            }

            const isNegative = data.growth?.includes("-");
            const growthValue = data.growth?.replace("-", "") || "0%";
            const arrowColor = isNegative ? "#F04438" : "#2AD192";
            const isProfitValue = Number(data.y) >= 0;

            const arrowPath = isNegative
              ? "M12 22L5 15H10V2H14V15H19L12 22Z" // ↓
              : "M12 2L19 9H14V22H10V9H5L12 2Z"; // ↑

            return `
              <div style="
                background: #000;
                color: white;
                border-radius: 12px;
                padding: 12px 16px;
                font-family: 'Inter', sans-serif;
                font-size: 14px;
                box-shadow: 0px 4px 12px rgba(0,0,0,0.15);
                width: 180px;
                display: flex;
                flex-direction: column;
              ">
                <div style="font-weight: 500; font-size: 14px;">${
                  data.x
                } ${year}</div>
                <div style="font-size: 20px; font-weight: 500; margin: 4px 0; color: ${
                  isProfitValue ? "#2AD192" : "#F04438"
                };">$${Math.abs(Number(data.y)).toLocaleString()}</div>
                <div style="font-size: 12px; color: #FFA07A;">${
                  isProfitValue ? "Profit" : "Loss"
                }</div>
                <div style="margin-top: 6px;">
                  <span style="
                    display: inline-flex;
                    align-items: center;
                    gap: 6px;
                    vertical-align: middle;
                  ">
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="${arrowColor}" xmlns="http://www.w3.org/2000/svg">
                      <path d="${arrowPath}"/>
                    </svg>
                    <span style="color: ${arrowColor}; font-weight: 500;">
                      ${growthValue}
                    </span>
                  </span>
                </div>
              </div>
            `;
          },
        },
      },
      series: [
        {
          name: "Profit & Loss",
          data: chartData,
        },
      ],
    };
  }, [getSalesReportListData?.data?.profit_loss_revenue, year]);

  return (
    <div>
      <div className="flex justify-between items-start">
        <div>
          <h6 className="font-Montserrat font-medium text-xl mb-3">
            Sales Reports
          </h6>
          <Breadcrumb
            breadCrumbParent="Home"
            breadCrumbParent2="Reports"
            breadCrumbActive="Sales Report"
            breadCrumbTitleKey="/"
            classes=""
          />
        </div>
      </div>
      {loading ? (
        <RippleLoader />
      ) : (
        <>
          <div className="mt-8">
            <Revenue />
          </div>
          <div className="bg-white rounded-lg shadow p-6 border border-borderLine mt-6">
            <div>
              <h2 className="text-xl font-normal font-Montserrat text-primaryBlacks">
                Total Sales:{" "}
                <span className="font-bold text-lg">
                  {dateRange?.[0] && dateRange?.[1]
                    ? `$${totalSales.toLocaleString()}`
                    : `$${getSalesReportListData?.data?.sales?.Total_Sales}`}
                </span>
              </h2>
            </div>
            <div className="mb-6 flex justify-between items-center">
              <h2 className="text-xl font-normal font-Montserrat text-primaryBlacks">
                Sales Breakdown
              </h2>
              <div className="flex flex-wrap items-center gap-2">
                {/* Company Dropdown */}
                <div className="w-full sm:w-[200px] md:w-[220px] -mt-1 min-w-[160px]">
                  <ReactSelect
                    className="w-full text-black p-0 font-Proxima"
                    id="company"
                    placeholder="Company"
                    options={companyOptions}
                    isClearable
                    getOptionValue={(option) => option?.value}
                    getOptionLabel={(option) => option?.label}
                    value={company}
                    onChange={handleCompanyChange}
                    styles={{
                      control: (base) => ({
                        ...base,
                        border: `1px solid #0000003B`,
                        borderColor: company ? "#F15B29" : "#0000003B",
                        borderRadius: "0.3rem",
                        boxShadow: "none",
                        "&:hover": {
                          border: "1px solid #0000003B",
                        },
                      }),
                      option: (base, state) => ({
                        ...base,
                        backgroundColor: state.isSelected ? "#F15B29" : "white",
                        "&:active": {
                          backgroundColor: state.isSelected
                            ? "#F15B29"
                            : "rgba(0, 0, 0, 0.04)",
                        },
                      }),
                    }}
                  />
                </div>

                {/* Date Range */}
                <div className="w-full sm:w-[220px] md:w-[240px] -mt-1 min-w-[160px]">
                  <div className="text-black border border-borderTheme/80 flex justify-between relative rounded w-full px-2 py-[7px] items-center transition duration-300 ease-in-out">
                    <ReactDatePicker
                      selected={dateRange[0]}
                      onChange={(dates) => setDateRange(dates)}
                      selectsRange
                      isClearable
                      startDate={dateRange[0]}
                      endDate={dateRange[1]}
                      maxDate={new Date()}
                      dateFormat="dd/MM/yyyy"
                      placeholderText="Date Range"
                      className="w-full"
                    />
                  </div>
                </div>

                {/* Export Button */}
                <div className="-mt-1">
                  <ExportButton
                    value="EXPORT CSV"
                    onClick={handleSalesBreakdownExportClick}
                  />
                </div>
              </div>
            </div>
            <CustomTable
              overflowVisibleLg={true}
              columnHeaders={<ColumnHeaders />}
              dataRows={<DataRows />}
              data={getSalesReportListData?.data?.sales}
              loading={false}
              showPagination={true}
              setPaginationCurrentLimit={setPageLimit}
              paginationCurrentLimit={pageLimit}
              current={pageCount}
              total={getSalesReportListData?.data?.sales?.total}
              paginationData={(crPage) => setPageCount(crPage)}
              lastPage={Math.ceil(
                getSalesReportListData?.data?.sales?.total / pageLimit
              )}
            />
          </div>

          {/* PNL Section */}
          <div className="bg-white rounded-lg shadow p-6 border border-borderLine mt-6">
            <div className="flex justify-between items-center mb-4">
              <div className="flex items-center gap-2">
                <h2 className="text-2xl font-normal font-Montserrat text-primaryBlacks">
                  Profit & Loss (PNL)
                </h2>
              </div>
            </div>
            <div className="mt-4">
              <div className="flex justify-between items-center mb-6">
                <div className="flex items-center gap-4">
                  <div>
                    <p className="text-sm text-textSecondary font-Proxima">
                      Total Sales
                    </p>
                    <p className="text-2xl font-bold text-primaryBlack mt-1">
                      $
                      {totalSales.toLocaleString(undefined, {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                    </p>
                  </div>
                </div>
              </div>
              <div className="mt-6">
                {pnlChartData &&
                  pnlChartData.series &&
                  pnlChartData.series[0]?.data && (
                    <Chart
                      options={pnlChartData.options}
                      series={pnlChartData.series}
                      height="300"
                      type="bar"
                    />
                  )}
              </div>
            </div>
          </div>

          {/* Technician Earnings Table */}
          <div className="bg-white rounded-lg shadow p-6 border border-borderLine mt-6">
            <div className="mb-6 flex justify-between items-center">
              <div>
                <h2 className="text-xl font-normal font-Montserrat text-primaryBlacks">
                  Technician Earnings
                </h2>
                <p className="text-sm text-textSecondary mt-1">
                  Total Earnings:{" "}
                  <span className="text-black font-semibold">
                    $
                    {totalTechnicianEarnings.toLocaleString(undefined, {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}
                  </span>
                </p>
              </div>
            </div>
            <CustomTable
              overflowVisibleLg={true}
              columnHeaders={<TechnicianEarningsHeaders />}
              dataRows={<TechnicianEarningsRows />}
              data={technicianEarningsTableData}
              loading={false}
              showPagination={true}
              paginationCurrentLimit={technicianPageLimit}
              setPaginationCurrentLimit={handleTechnicianPageLimitChange}
              current={technicianPage}
              total={technicianEarningsTableData.total || 0}
              paginationData={handleTechnicianPageChange}
              lastPage={technicianEarningsTableData.last_page || 1}
            />
          </div>
        </>
      )}
    </div>
  );
};

export default SalesReports;
